import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { VirtualAdminTable, type VirtualColumnDef } from '@/components/ui/virtual-table';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { AdminSectionCard } from '@/components/admin/AdminSectionCard';
import { toAdminUserDetailPath } from '@/lib/routes/admin-route-catalog';
import { listSubscriptions, type AdminSubscription } from '@/services/admin-api';
import { format } from 'date-fns';

function getTierVariant(tier: string): 'default' | 'info' | 'secondary' {
  switch (tier) {
    case 'PRO': return 'info'
    case 'FREE': return 'secondary'
    default: return 'default'
  }
}

function getStatusVariant(status: string): 'success' | 'info' | 'warning' | 'destructive' | 'outline' {
  switch (status) {
    case 'ACTIVE': return 'success'
    case 'TRIALING': return 'info'
    case 'PAST_DUE': return 'warning'
    case 'CANCELED': return 'destructive'
    default: return 'outline'
  }
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-subscriptions', page, pageSize],
    queryFn: () => listSubscriptions({ page, pageSize }),
  });

  const subscriptions = data?.data || [];
  const pagination = data?.pagination;

  const subColumns: VirtualColumnDef[] = [
    { key: 'user', header: 'User' },
    { key: 'tier', header: 'Tier' },
    { key: 'status', header: 'Status' },
    { key: 'trialEnd', header: 'Trial End' },
    { key: 'created', header: 'Created' },
  ];

  const renderSubRow = (sub: AdminSubscription) => (
    <>
      <div role="cell" className="p-4 align-middle">
        <div>
          <div className="font-medium">{sub.user.name || 'No name'}</div>
          <div className="text-xs text-muted-foreground">{sub.user.email}</div>
        </div>
      </div>
      <div role="cell" className="p-4 align-middle">
        <Badge variant={getTierVariant(sub.tier)}>{sub.tier}</Badge>
      </div>
      <div role="cell" className="p-4 align-middle">
        <Badge variant={getStatusVariant(sub.status)}>{sub.status}</Badge>
      </div>
      <div role="cell" className="p-4 align-middle text-muted-foreground">
        {sub.trialEndDate
          ? format(new Date(sub.trialEndDate), 'MMM d, yyyy')
          : '-'}
      </div>
      <div role="cell" className="p-4 align-middle text-muted-foreground">
        {format(new Date(sub.createdAt), 'MMM d, yyyy')}
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader title="Subscriptions" description="Manage user subscriptions" />

      <AdminSectionCard
        title="All Subscriptions"
        description={`${pagination?.total.toLocaleString() ?? '0'} subscriptions total`}
      >
        <VirtualAdminTable
          data={subscriptions}
          columns={subColumns}
          renderRow={renderSubRow}
          onRowClick={(sub) => navigate(toAdminUserDetailPath(sub.userId))}
          rowClassName="cursor-pointer"
          loading={isLoading}
          emptyMessage="No subscriptions found"
        />

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </AdminSectionCard>
    </div>
  );
}
